/**
 * MyExams.jsx — Phase 2B Student Portal
 *
 * Student's view of their scheduled exams, eligibility, registrations, and attempts.
 *
 * Critical invariants:
 *   • Renders only own registrations (student_id = authenticated user)
 *   • Sessions loaded per offering so the student can start an attempt
 *   • AttemptCreate requires session_id: for each CONFIRMED registration, the
 *     student chooses an OPEN session
 *   • Error codes surfaced: 403, 404, 409, 422
 *   • Submitted attempts are visibly read-only (no re-entry link)
 */
import React, { useEffect, useState, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ClipboardList, Calendar, CheckCircle, Clock, AlertTriangle, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import { DeliveryStatusBadge } from "../components/assessment-delivery/DeliveryStatusBadge";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

function authH() {
  return { Authorization: `Bearer ${localStorage.getItem("academiaos_token")}` };
}

async function apiFetch(path, opts = {}) {
  const r = await fetch(`${API}${path}`, {
    headers: { ...authH(), "Content-Type": "application/json" },
    ...opts,
  });
  if (!r.ok) {
    let msg;
    try {
      const body = await r.json();
      msg =
        body?.detail?.message ||
        body?.detail?.conflict ||
        (typeof body?.detail === "string" ? body.detail : null) ||
        body?.message ||
        r.statusText;
    } catch {
      msg = r.statusText;
    }
    const err = Object.assign(new Error(msg || r.statusText), { status: r.status });
    throw err;
  }
  return r.json();
}

export default function MyExams() {
  const { user } = useAuth();
  const navigate = useNavigate();

  const [registrations, setRegistrations]         = useState([]);
  const [attempts, setAttempts]                   = useState([]);
  const [sessionsByOffering, setSessionsByOffering] = useState({});
  const [loading, setLoading]                     = useState(true);
  const [starting, setStarting]                   = useState(null); // reg.id while creating

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [regList, attList] = await Promise.all([
        apiFetch("/delivery/registrations"),
        apiFetch("/delivery/attempts"),
      ]);
      setRegistrations(regList);
      setAttempts(attList);

      // Load sessions for each CONFIRMED registration's offering
      const confirmedOfferingIds = [
        ...new Set(regList.filter(r => r.status === "CONFIRMED").map(r => r.offering_id)),
      ];
      if (confirmedOfferingIds.length > 0) {
        const entries = await Promise.all(
          confirmedOfferingIds.map(async (oid) => {
            try {
              const sessions = await apiFetch(`/delivery/offerings/${oid}/sessions`);
              return [oid, sessions];
            } catch {
              return [oid, []];
            }
          })
        );
        setSessionsByOffering(Object.fromEntries(entries));
      }
    } catch (e) {
      toast.error("Load failed: " + e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Group attempts by registration_id
  const attemptsByReg = attempts.reduce((acc, a) => {
    (acc[a.registration_id] = acc[a.registration_id] || []).push(a);
    return acc;
  }, {});

  const handleStartAttempt = async (registration, sessionId) => {
    setStarting(registration.id + sessionId);
    try {
      const attempt = await apiFetch("/delivery/attempts", {
        method: "POST",
        body: JSON.stringify({
          registration_id: registration.id,
          session_id: sessionId,
        }),
      });
      toast.success("Attempt created — opening workspace");
      navigate(`/my-exams/attempt/${attempt.id}`);
    } catch (e) {
      if (e.status === 409) {
        toast.warning(`Cannot start: ${e.message}`);
      } else if (e.status === 422) {
        toast.error(`Validation: ${e.message}`);
      } else {
        toast.error("Start failed: " + e.message);
      }
    } finally {
      setStarting(null);
    }
  };

  if (!user) return null;

  return (
    <div className="min-h-screen bg-slate-950" data-testid="my-exams-page">
      {/* ── Header ─────────────────────────────────────────────────────── */}
      <div className="border-b border-slate-800 bg-slate-900/60 px-8 py-6">
        <div className="max-w-4xl mx-auto flex items-center justify-between">
          <div>
            <div className="flex items-center gap-3 mb-1">
              <ClipboardList className="w-5 h-5 text-emerald-400" />
              <h1 className="text-xl font-semibold text-slate-100">My Exams</h1>
            </div>
            <p className="text-sm text-slate-400">Your scheduled assessments and attempt history</p>
          </div>
          <button
            type="button"
            onClick={load}
            disabled={loading}
            data-testid="my-exams-refresh"
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs text-slate-400
                       border border-slate-700 bg-slate-900/60 hover:bg-slate-800 transition-colors"
          >
            <RefreshCw className={`w-3 h-3 ${loading ? "animate-spin" : ""}`} />
            Refresh
          </button>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-8 py-8">
        {loading ? (
          <div className="text-sm text-slate-500 py-12 text-center" data-testid="my-exams-loading">
            Loading your exams…
          </div>
        ) : registrations.length === 0 ? (
          <div className="rounded-xl border border-dashed border-slate-700 p-12 text-center"
               data-testid="my-exams-empty">
            <ClipboardList className="w-10 h-10 text-slate-600 mx-auto mb-3" />
            <p className="text-slate-400 text-sm">No exams registered.</p>
            <p className="text-slate-600 text-xs mt-1">
              Your registrations will appear here once confirmed by the exam office.
            </p>
          </div>
        ) : (
          <div className="space-y-4" data-testid="my-exams-list">
            {registrations.map(reg => {
              const myAttempts      = attemptsByReg[reg.id] || [];
              const inProgressAttempt = myAttempts.find(a => a.status === "IN_PROGRESS");
              const notStartedAttempt = myAttempts.find(a => a.status === "NOT_STARTED");
              const openSessions    = (sessionsByOffering[reg.offering_id] || [])
                .filter(s => s.status === "OPEN");
              const submittedCount = myAttempts.filter(
                a => a.status === "SUBMITTED" || a.status === "FORCE_SUBMITTED"
              ).length;
              const maxAttempts = reg.max_attempts ?? 1;
              const attemptLimitReached = submittedCount >= maxAttempts;
              const canCreate = reg.status === "CONFIRMED"
                && !inProgressAttempt
                && !notStartedAttempt
                && !attemptLimitReached
                && openSessions.length > 0;

              return (
                <div
                  key={reg.id}
                  className="rounded-xl border border-slate-800 bg-slate-900/60 overflow-hidden"
                  data-testid={`exam-card-${reg.id}`}
                >
                  {/* Registration header */}
                  <div className="px-6 py-5 border-b border-slate-800">
                    <div className="flex items-start justify-between gap-4">
                      <div>
                        <div className="flex items-center gap-3 mb-2">
                          <DeliveryStatusBadge status={reg.status} />
                          {reg.offering_code && (
                            <span className="text-xs text-slate-500 font-mono">
                              {reg.offering_code}
                            </span>
                          )}
                        </div>
                        <p className="text-base font-medium text-slate-100">
                          {reg.offering_title || reg.offering_code || "Assessment"}
                        </p>
                        <p className="text-xs text-slate-500 mt-1">
                          Registration: {reg.id?.slice(0, 12)}…
                        </p>
                      </div>

                      {/* Action buttons */}
                      {reg.status === "CONFIRMED" && (
                        <div className="flex flex-col gap-2 min-w-0">
                          {inProgressAttempt && (
                            <Link
                              to={`/my-exams/attempt/${inProgressAttempt.id}`}
                              data-testid={`continue-attempt-${reg.id}`}
                              className="px-4 py-2 rounded-lg text-sm font-semibold bg-sky-700
                                         hover:bg-sky-600 text-white transition-colors text-center"
                            >
                              Continue
                            </Link>
                          )}
                          {notStartedAttempt && (
                            <Link
                              to={`/my-exams/attempt/${notStartedAttempt.id}`}
                              data-testid={`start-attempt-link-${reg.id}`}
                              className="px-4 py-2 rounded-lg text-sm font-semibold bg-emerald-700
                                         hover:bg-emerald-600 text-white transition-colors text-center"
                            >
                              Start Attempt
                            </Link>
                          )}
                          {canCreate && openSessions.map(sess => (
                            <button
                              key={sess.id}
                              type="button"
                              disabled={starting === reg.id + sess.id}
                              onClick={() => handleStartAttempt(reg, sess.id)}
                              data-testid={`create-attempt-${reg.id}-${sess.id}`}
                              className="px-4 py-2 rounded-lg text-sm font-semibold bg-emerald-700
                                         hover:bg-emerald-600 text-white transition-colors disabled:opacity-50"
                            >
                              {starting === reg.id + sess.id ? "Starting…" : `Start: ${sess.title}`}
                            </button>
                          ))}
                          {reg.status === "CONFIRMED" && !inProgressAttempt && !notStartedAttempt && openSessions.length === 0 && !attemptLimitReached && (
                            <span className="text-xs text-slate-500 italic px-1">
                              No open sessions yet
                            </span>
                          )}
                          {reg.status === "CONFIRMED" && attemptLimitReached && !inProgressAttempt && !notStartedAttempt && (
                            <span
                              className="text-xs text-amber-400 italic px-1"
                              data-testid={`attempt-limit-reached-${reg.id}`}
                            >
                              Attempt limit reached ({maxAttempts}/{maxAttempts} used)
                            </span>
                          )}
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Available sessions info */}
                  {reg.status === "CONFIRMED" && openSessions.length > 0 && (
                    <div className="px-6 py-3 bg-emerald-950/10 border-b border-slate-800/60">
                      <p className="text-xs text-emerald-400 flex items-center gap-1.5">
                        <Clock className="w-3 h-3" />
                        {openSessions.length} open session{openSessions.length !== 1 ? "s" : ""} available
                        {" · "}{openSessions[0]?.duration_minutes} min
                        {openSessions[0]?.scheduled_date ? ` · ${openSessions[0].scheduled_date}` : ""}
                      </p>
                    </div>
                  )}

                  {/* Attempt history */}
                  {myAttempts.length > 0 && (
                    <div className="px-6 py-4 bg-slate-900/30">
                      <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-3">
                        Attempts ({myAttempts.length})
                      </p>
                      <div className="space-y-2">
                        {myAttempts.map(a => (
                          <div
                            key={a.id}
                            className="flex items-center gap-3"
                            data-testid={`attempt-history-${a.id}`}
                          >
                            <DeliveryStatusBadge status={a.status} size="xs" />
                            <span className="text-xs text-slate-400">
                              Attempt #{a.attempt_number}
                            </span>
                            {a.status === "SUBMITTED" && (
                              <span className="text-xs text-slate-500 ml-auto flex items-center gap-1">
                                <CheckCircle className="w-3 h-3 text-green-500" />
                                Submitted {a.submitted_at?.slice(0, 10)}
                                <span className="text-slate-600 ml-1 italic">(read-only)</span>
                              </span>
                            )}
                            {a.status === "IN_PROGRESS" && (
                              <Link
                                to={`/my-exams/attempt/${a.id}`}
                                data-testid={`resume-attempt-${a.id}`}
                                className="text-xs text-sky-400 hover:text-sky-300 ml-auto transition-colors"
                              >
                                Resume →
                              </Link>
                            )}
                            {a.status === "NOT_STARTED" && (
                              <Link
                                to={`/my-exams/attempt/${a.id}`}
                                data-testid={`open-attempt-${a.id}`}
                                className="text-xs text-emerald-400 hover:text-emerald-300 ml-auto transition-colors"
                              >
                                Open →
                              </Link>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
